import Box from "@mui/material/Box";
import ModalBasicLayout from "../../../components/Modal/ModalBasicLayout";
import { useTranslationApp } from "../../../lib/i18next";
import {
  BUTTON_CLOSE,
  BUTTON_SAVE,
  EMAIL_PLACEHOLDER,
  FIELD_EMAIL_REGISTERED,
  FIELD_EMAIL_VALID,
  FIELD_PASSWORD_INSECURE,
  FIELD_REQUIRED,
  MODAL_USER_ARIA_LABEL,
  MODAL_USER_DESCRIPTION_EDIT,
  MODAL_USER_TITLE_EDIT,
  NAME_PLACEHOLDER,
  PASSWORD_CLARIFICATION_IN_EDIT_USER,
  PASSWORD_PLACEHOLDER,
  READY,
  USERS_USER_SAVED,
} from "../../../i18n/keysTranslations";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import ContainerModalActions from "../../../components/Containers/ContainerModalActions";
import { useForm } from "react-hook-form";
import useFocusErrorForm from "../../../hooks/useFocusErrorForm";
import TextField from "../../../components/Form/TextField";
import { REGEX_EMAIL, REGEX_PASSWORD } from "../../../utils/regex";
import { useEffect, useState } from "react";
import IconButton from "../../../components/Buttons/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { auth } from "../../../lib/firebase";
import { fetchSignInMethodsForEmail } from "firebase/auth";
import { startManageUser } from "../../../actions/user";
import { SimpleAlert } from "../../../components/Alerts/Alerts";
import { ALERT_ICON_TYPE_SUCCESS } from "../../../utils/constants";

const SuperAdminSelfEditModal = ({ open, onCloseModal, userID, user }) => {
  const [isFetching, setIsFetching] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { control, formState, handleSubmit, setValue, setError } = useForm({
    defaultValues: {
      name: "",
      email: "",
      password: "",
    },
  });

  useFocusErrorForm(formState);
  const { errors } = formState;

  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const businessNameState = useSelectorApp((state) => state.user.businessName);
  const NAME_BUSINESS = businessNameState || "";

  const onChangeShowPassword = () => setShowPassword((prev) => !prev);

  const onSubmit = async (formValues) => {
    const { email } = formValues;

    setIsFetching(true);

    if (user?.email !== email) {
      const result = await fetchSignInMethodsForEmail(auth, email);
      if (result.length > 0) {
        setError("email", {
          type: "manual",
          message: t(FIELD_EMAIL_REGISTERED),
        });
        setIsFetching(false);
        return;
      }
    }

    const response = await dispatch(
      startManageUser({
        name: formValues.name,
        email: formValues.email,
        password: formValues.password,
        userID,
      })
    );

    if (response) {
      SimpleAlert({
        title: t(READY),
        text: t(USERS_USER_SAVED),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      onCloseModal();
    }

    setIsFetching(false);
  };

  useEffect(() => {
    if (!userID || !user) {
      onCloseModal();
      return;
    }

    setValue("name", user.name);
    setValue("email", user.email);
    // eslint-disable-next-line
  }, [userID, user]);

  return (
    <ModalBasicLayout
      modalOpen={open}
      onCloseModal={onCloseModal}
      fullWidth={true}
      maxWidth="xs"
      scroll="paper"
      ariaLabel={t(MODAL_USER_ARIA_LABEL)}
      title={t(MODAL_USER_TITLE_EDIT)}
      description={t(MODAL_USER_DESCRIPTION_EDIT, { NAME_BUSINESS })}
      sxTitle={{ color: "background.paperContrastText" }}
      sxDescription={{ color: "background.paperContrastText" }}
    >
      <Box
        onSubmit={handleSubmit(onSubmit)}
        component="form"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <TextField
          fullWidth
          label={t(NAME_PLACEHOLDER)}
          name="name"
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
          errors={errors}
        />
        <TextField
          fullWidth
          id="email"
          label={t(EMAIL_PLACEHOLDER)}
          name="email"
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            validate: {
              emailValid: (value) => {
                if (!REGEX_EMAIL.test(value)) {
                  return t(FIELD_EMAIL_VALID);
                }
              },
            },
          }}
          errors={errors}
        />
        <TextField
          fullWidth
          id="password"
          name="password"
          label={t(PASSWORD_PLACEHOLDER)}
          type={showPassword ? "text" : "password"}
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <IconButton onClick={onChangeShowPassword}>
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
          helperText={t(PASSWORD_CLARIFICATION_IN_EDIT_USER)}
          control={control}
          rules={{
            validate: (value) => {
              if (value && !REGEX_PASSWORD.test(value)) {
                return t(FIELD_PASSWORD_INSECURE);
              }
            },
          }}
          errors={errors}
        />
        <ContainerModalActions
          hasPadding={false}
          propsPrimaryButton={{
            showButton: true,
            text: BUTTON_SAVE,
            loading: isFetching,
            onClick: () => {},
          }}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CLOSE,
            loading: isFetching,
            onClick: onCloseModal,
          }}
        />
      </Box>
    </ModalBasicLayout>
  );
};

export default SuperAdminSelfEditModal;
